'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Zap, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

const DEMO_CREDENTIALS = {
  email: 'maya@voltvision18.app',
  password: 'Creator2026!',
};

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c0-4.67-1.87-8.6-4.89-11.24l-3.57 2.77c.98.66 1.8 1.57 2.32 2.65H12c-2.78 0-5.15 1.11-6.93 2.9L1.5 9.67C3.69 7.03 7.07 5.25 12 5.25c2.71 0 5.14.98 7.02 2.59l2.61-2.61C19.26 3.24 15.84 2 12 2 6.48 2 1.84 5.25.29 9.91L4.1 12.7C4.94 9.98 8.22 8 12 8v4l3.56-2.77C14.42 8.77 13.25 8.5 12 8.5z" fill="#34A853" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.05-3.71 1.05-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const { t } = useLanguage();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = (data: LoginFormValues) => {
    setAuthError('');
    setIsLoading(true);

    // Backend integration point: POST /api/auth/login with { email, password }
    setTimeout(() => {
      if (
        data.email === DEMO_CREDENTIALS.email &&
        data.password === DEMO_CREDENTIALS.password
      ) {
        toast.success(t.auth.welcomeBack2);
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } else {
        setAuthError(t.auth.invalidCredentials);
        setIsLoading(false);
      }
    }, 800);
  };

  const fillDemo = () => {
    setValue('email', DEMO_CREDENTIALS.email);
    setValue('password', DEMO_CREDENTIALS.password);
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-extrabold text-foreground">{t.auth.welcomeBack}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t.auth.signInSubtitle}</p>
      </div>

      {/* Google Sign In */}
      <button
        type="button"
        className="btn-ghost w-full py-3 text-sm flex items-center justify-center gap-2"
        onClick={() => toast.info(t.auth.googleComingSoon)}
      >
        <GoogleIcon />
        {t.auth.continueWithGoogle}
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">{t.auth.orEmail}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Auth Error */}
      {authError && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-danger/10 border border-danger/30">
          <AlertCircle size={14} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-xs text-danger">{authError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {/* Email */}
        <div>
          <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
            {t.auth.emailAddress}
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="input-field w-full px-4 py-3 text-sm"
            {...register('email', {
              required: t.auth.emailRequired,
              pattern: { value: /^\S+@\S+\.\S+$/, message: t.auth.emailInvalid },
            })}
          />
          {errors.email && (
            <p className="text-xs text-danger mt-1.5">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="text-sm font-semibold text-foreground">
              {t.auth.password}
            </label>
            <button
              type="button"
              className="text-xs text-primary hover:underline font-medium"
              onClick={() => toast.info(t.auth.forgotPasswordSoon)}
            >
              {t.auth.forgotPassword}
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Your password"
              className="input-field w-full px-4 py-3 text-sm pr-11"
              {...register('password', {
                required: t.auth.passwordRequired,
                minLength: { value: 6, message: t.auth.passwordMin },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-danger mt-1.5">{errors.password.message}</p>
          )}
        </div>

        {/* Remember Me */}
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 rounded accent-primary"
            {...register('rememberMe')}
          />
          <span className="text-sm text-muted-foreground">{t.auth.rememberMe}</span>
        </label>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              {t.auth.signingIn}
            </>
          ) : (
            <>
              <Zap size={15} />
              {t.auth.signInBtn}
            </>
          )}
        </button>
      </form>

      {/* Demo Credentials Box */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-primary uppercase tracking-wider">{t.auth.demoAccount}</span>
          <button
            type="button"
            onClick={fillDemo}
            className="text-xs font-semibold text-accent hover:text-accent/80 transition-colors border border-accent/30 px-2.5 py-1 rounded-lg hover:bg-accent/10"
          >
            {t.auth.useDemo}
          </button>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">Email</span>
            <span className="text-[11px] font-mono text-foreground">{DEMO_CREDENTIALS.email}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">{t.auth.password}</span>
            <span className="text-[11px] font-mono text-foreground">{DEMO_CREDENTIALS.password}</span>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        {t.auth.noAccount}{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          className="text-primary font-semibold hover:underline"
        >
          {t.auth.createFree}
        </button>
      </p>
    </div>
  );
}